import React, { useEffect, useMemo, useState } from 'react';

// Utils
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { useMutation, useQuery } from 'react-query';
import withAuth from '../../../utils/hocs/withAuth';
import withSidebar from '../../../utils/hocs/withSidebar';
import SCOPE from '../../../utils/gate/scope';
import withPermission from '../../../utils/hocs/withPermission';
import useNotification from '../../../utils/hooks/useNotification';
import campMarketingSchema from '../../../validations/products/camp/marketingSchema';
import buildTabs from '../../../utils/tools/buildTabs';
import useForm from '../../../utils/hooks/useForm';

// Services
import useCampService from '../../../services/campService';

// Icons
import { ReactComponent as FileIcon } from '../../../assets/icons/file.svg';
import { ReactComponent as CalendarIcon } from '../../../assets/icons/calendar.svg';
import { ReactComponent as PictureIcon } from '../../../assets/icons/picture.svg';
import { ReactComponent as InfoIcon } from '../../../assets/icons/info.svg';

// Components
import PageWrapper from '../../../components/layout/PageWrapper/PageWrapper';
import General from '../../../components/views/establishment/Products/Camps/Sections/General/General';
import Format from '../../../components/views/establishment/Products/Camps/Sections/Format/Format';
import LayoutWithTabs from '../../../components/layout/LayoutWithTabs/LayoutWithTabs';
import Medias from '../../../components/views/establishment/Products/Camps/Sections/Medias/Medias';
import Features from '../../../components/views/establishment/Products/Camps/Sections/Features/Features';
import SkeletonLoader from '../../../components/molecules/SkeletonLoader/SkeletonLoader';
import Button from '../../../components/atoms/Button/Button';

function CampMarketing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { displayNotification } = useNotification();
  const { handleScrollToError } = useForm();

  // URL query params
  const [searchParams] = useSearchParams();
  const campId = useMemo(() => searchParams.get('id'), [searchParams]);
  useEffect(() => { if (!campId) navigate('/products/camps'); }, []);

  const [camp, setCamp] = useState(null);
  const [initialValues, setInitialValues] = useState({
    isNovelty: false,
    name: '',
    sport: null,
    type: null,
    duration: 1,
    startDay: null,
    format: null,
    features: [],
    tags: [],
    mainImageMedia: null,
    sliderImageMedias: [],
    videoUrl: '',
  });

  const { getCamp, putCamp } = useCampService();

  const getFormattedCamp = (values) => ({
    isNovelty: values.isNovelty,
    name: values.name,
    sport: values.sport ? { value: values.sport, label: t(`camps.sports.${values.sport}`) } : null,
    type: values.type ? { value: values.type, label: t(`camps.types.${values.type}`) } : null,
    duration: values.duration,
    startDay: values.startDay
      ? { value: values.startDay, label: t(`camps.startDays.${values.startDay}`) }
      : null,
    format: values.format
      ? { value: values.format, label: t(`camps.formats.${values.format}`) }
      : null,
    features: values.features?.length
      ? values.features.map((feature) => (
        { value: feature, label: t(`camps.features.${feature}`) }
      ))
      : [],
    tags: values.tags?.length
      ? values.tags.map((feature) => (
        { value: feature, label: t(`camps.tags.${feature}`) }
      ))
      : [],
    mainImageMedia: values.mainImageMedia,
    sliderImageMedias: values.sliderImageMedias,
    videoUrl: values.videoUrl || '',
  });

  const { isFetching } = useQuery('getCamp', () => getCamp(campId), {
    enabled: !!campId,
    onSuccess: (res) => {
      if (res?.data) {
        setCamp(res.data);
        setInitialValues(getFormattedCamp(res.data));
      }
    },
    onError: () => {
      navigate('/products/camps');
    },
  });

  const { isLoading: isPutCampLoading, mutate: putCampMutate } = useMutation(
    (values) => putCamp({ campId, camp: values }),
    {
      onSuccess: (res) => {
        displayNotification({
          message: t('common.successfullySaved'),
        });

        if (res?.data) {
          setInitialValues(getFormattedCamp(res.data));
        }
      },
      onError: (error) => {
        displayNotification({
          message: t(error?.response?.data?.code
            ? `code_errors.${error.response.data.code}`
            : 'common.saveError'),
          color: 'error',
        });
      },
    },
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (values) {
        putCampMutate({
          isNovelty: values.isNovelty,
          name: values.name,
          sport: values.sport?.value,
          type: values.type?.value,
          duration: values.duration,
          startDay: values.startDay?.value,
          format: values.format?.value,
          features: values.features?.map((feature) => feature.value) || [],
          tags: values.tags?.map((tag) => tag.value) || [],
          mainImageMediaId: values.mainImageMedia?.id,
          sliderImageMediaIds: values.sliderImageMedias?.map((media) => media.id),
          videoUrl: values.videoUrl || null,
        });
      }
    },
    validationSchema: campMarketingSchema,
    validateOnBlur: true,
  });

  const getTabComponent = (TabComponent, props) => (
    <TabComponent
      formik={{
        values: formik.values,
        handleChange: formik.handleChange,
        handleBlur: formik.handleBlur,
        setFieldValue: formik.setFieldValue,
        setFieldTouched: formik.setFieldTouched,
        touched: formik.touched,
        errors: formik.errors,
      }}
      {...props}
    />
  );

  const tabs = buildTabs([
    {
      label: 'camps.marketing.sections.general',
      component: getTabComponent(General, {}),
      icon: <FileIcon />,
    },
    {
      label: 'camps.marketing.sections.format',
      component: getTabComponent(Format, { isCreation: false }),
      icon: <CalendarIcon />,
    },
    {
      label: 'camps.marketing.sections.medias',
      component: getTabComponent(Medias, {}),
      icon: <PictureIcon />,
    },
    {
      label: 'camps.marketing.sections.features',
      component: getTabComponent(Features, {}),
      icon: <InfoIcon />,
    },
  ]);

  return (
    <PageWrapper>
      <LayoutWithTabs
        tabs={tabs}
        breadcrumb={isFetching ? null : [
          { label: t('camps.title'), href: '/products/camps' },
        ]}
        title={camp?.name}
        isFetching={isFetching}
        skeletonLoader={(
          <>
            <SkeletonLoader className="m-b-30" height={30} />

            {tabs?.map((_, index) => (
              <React.Fragment key={index}>
                <SkeletonLoader className="m-b-20" height={50} />
                <SkeletonLoader className="m-b-50" height={200} />
              </React.Fragment>
            ))}
          </>
        )}
        moreContent={camp && !isFetching ? (
          <div className="d-flex a-center j-end sticky-actions p-r-30 mobile-p-r-0">
            <Button
              full={isMobile}
              onClick={() => {
                formik.submitForm().then(() => handleScrollToError(formik.errors));
              }}
              isLoading={isPutCampLoading}
              disabled={!formik.dirty}
            >
              {t('common.saveModifications')}
            </Button>
          </div>
        ) : null}
      />
    </PageWrapper>
  );
}

export default withAuth(
  withPermission(
    withSidebar(CampMarketing, { withTabsBar: true }),
    {
      scopes: [SCOPE.update_ownEst_camp, SCOPE.create_ownEst_camp],
    },
  ),
);
